import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { cn } from "@/lib/utils";
import type { ProductResponse } from "@byk/shared";
import { PlacementModeToggle } from "./PlacementModeToggle";

interface Category {
  id: string;
  name: string;
  icon: string;
}

const CATEGORIES: Category[] = [
  { id: "sinks", name: "Sinks", icon: "🚰" },
  { id: "taps", name: "Taps", icon: "🚿" },
  { id: "ovens", name: "Ovens", icon: "🔥" },
  { id: "cooktops", name: "Cooktops", icon: "♨️" },
  { id: "surfaces", name: "Surfaces", icon: "🪨" },
  { id: "cabinets", name: "Cabinets", icon: "🗄️" },
  { id: "purifiers", name: "Purifiers", icon: "💧" },
];

interface ProductCardProps {
  product: ProductResponse;
  onDragStart: (productId: string) => void;
  onClickSelect: (productId: string) => void;
  isClickMode: boolean;
  isSelected: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onDragStart,
  onClickSelect,
  isClickMode,
  isSelected,
}) => {
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (isClickMode) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.setData("productId", product.id);
      e.dataTransfer.effectAllowed = "copy";
      onDragStart(product.id);
    },
    [product.id, onDragStart, isClickMode]
  );

  const handleClick = useCallback(() => {
    if (isClickMode) {
      onClickSelect(product.id);
    }
  }, [product.id, onClickSelect, isClickMode]);

  const dimensions = product.dimensions;
  const dimText = dimensions
    ? `${dimensions.width}x${dimensions.depth}x${dimensions.height}`
    : "";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
    >
      <div
        draggable={!isClickMode}
        onDragStart={handleDragStart}
        onClick={handleClick}
        className={cn(
          "group rounded-xl border p-2.5 transition-all",
          "bg-white/60 hover:bg-white",
          "shadow-sm hover:shadow-md",
          isClickMode
            ? "cursor-pointer hover:border-blue-300"
            : "cursor-grab active:cursor-grabbing",
          isSelected && "border-blue-500 bg-blue-50 ring-2 ring-blue-500/50"
        )}
        role={isClickMode ? "button" : undefined}
        aria-pressed={isClickMode ? isSelected : undefined}
        aria-label={
          isClickMode ? `${product.name}. Click to select for placement` : undefined
        }
      >
      {/* Compact layout */}
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 text-lg">
          {product.categoryId === "sinks" && "🚰"}
          {product.categoryId === "taps" && "🚿"}
          {product.categoryId === "ovens" && "🔥"}
          {product.categoryId === "cooktops" && "♨️"}
          {product.categoryId === "surfaces" && "🪨"}
          {product.categoryId === "cabinets" && "🗄️"}
          {product.categoryId === "purifiers" && "💧"}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-medium text-gray-900">
            {product.name}
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="truncate">{dimText} cm</span>
            {product.price && (
              <>
                <span className="text-gray-300">|</span>
                <span className="font-medium text-blue-600">
                  {product.price.toLocaleString()} {product.currency}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Action hint */}
        <div className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
          {isClickMode ? (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-600">
              Click
            </span>
          ) : (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              Drag
            </span>
          )}
        </div>
      </div>
      </div>
    </motion.div>
  );
};

interface CategorySectionProps {
  category: Category;
  products: ProductResponse[];
  isExpanded: boolean;
  onToggle: () => void;
  onDragStart: (productId: string) => void;
  onClickSelect: (productId: string) => void;
  isClickMode: boolean;
  selectedProductId: string | null;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  products,
  isExpanded,
  onToggle,
  onDragStart,
  onClickSelect,
  isClickMode,
  selectedProductId,
}) => {
  return (
    <div className="overflow-hidden">
      {/* Category header */}
      <button
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between px-3 py-2",
          "rounded-lg transition-colors",
          "hover:bg-gray-100",
          isExpanded && "bg-gray-50"
        )}
        aria-expanded={isExpanded}
        aria-controls={`category-${category.id}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-base">{category.icon}</span>
          <span className="text-sm font-medium text-gray-700">{category.name}</span>
          <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
            {products.length}
          </span>
        </div>
        <motion.svg
          className="h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>

      {/* Products */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            id={`category-${category.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2 overflow-hidden px-1 pb-2 pt-1"
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDragStart={onDragStart}
                onClickSelect={onClickSelect}
                isClickMode={isClickMode}
                isSelected={selectedProductId === product.id}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ProductCatalog: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["cabinets", "sinks"]) // Default expanded
  );

  const {
    products,
    setDraggedProduct,
    placementMode,
    pendingPlacement,
    startClickPlacement,
  } = useFloorPlanStore();

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    const query = searchQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.sku.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, ProductResponse[]> = {};
    CATEGORIES.forEach((cat) => {
      grouped[cat.id] = filteredProducts.filter((p) => p.categoryId === cat.id);
    });
    return grouped;
  }, [filteredProducts]);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }, []);

  const categoriesWithProducts = CATEGORIES.filter(
    (cat) => productsByCategory[cat.id].length > 0
  );

  return (
    <div className="flex h-full flex-col">
      {/* Search and Mode Toggle */}
      <div className="space-y-3 p-3">
        {/* Placement Mode Toggle */}
        <PlacementModeToggle />

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full rounded-xl border-0 py-2.5 pl-10 pr-4 text-sm",
              "bg-gray-100/80 placeholder-gray-400",
              "focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/50",
              "transition-all"
            )}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Category sections */}
      <div className="flex-1 overflow-y-auto px-2 pb-2" style={{ overscrollBehaviorY: 'contain' }}>
        {categoriesWithProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="mb-2 text-3xl">📦</div>
            <p className="text-sm text-gray-500">No products found</p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {categoriesWithProducts.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
                products={productsByCategory[category.id]}
                isExpanded={expandedCategories.has(category.id)}
                onToggle={() => toggleCategory(category.id)}
                onDragStart={setDraggedProduct}
                onClickSelect={startClickPlacement}
                isClickMode={placementMode === "click"}
                selectedProductId={pendingPlacement?.productId || null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2">
        <p className="text-center text-[11px] text-gray-400">
          {placementMode === "click"
            ? "Select product → Click canvas to place"
            : "Drag products onto the canvas"}
        </p>
      </div>
    </div>
  );
};

export default ProductCatalog;
